import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import LockIcon from '@mui/icons-material/Lock';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PageContainer from '../../../shared/PageContainer';
import GlowCard from '../../../shared/GlowCard';
import ProgressRing from '../../../shared/ProgressRing';
import CyberpunkTitle from '../../../shared/CyberpunkTitle';
import { useColors } from '../../../theme/ColorModeContext';
import { getTheoryStages } from '../../../api/Api';
import { getAcademyTrackForCategory } from '../skillTreeData';
import { getStageContent } from './stageRegistry';
import { TheoryStageData } from './types';
import FollowGate from './FollowGate';

interface PropsTheoryOverview {
    token: string;
    category: string;
}

const TheoryOverview = ({ token, category }: PropsTheoryOverview) => {
    const colors = useColors();
    const track = getAcademyTrackForCategory(category);

    const [stages, setStages] = useState<TheoryStageData[]>([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            const data = await getTheoryStages(token, category);
            if (data) setStages(data.stages);
            setLoaded(true);
        };
        fetchData();
    }, [token, category]);

    const solvedCount = stages.filter((stage) => stage.solved).length;
    const progress = stages.length > 0 ? (solvedCount / stages.length) * 100 : 0;

    return (
        <PageContainer>
            <CyberpunkTitle
                segments={[
                    { label: track.title, to: token ? `/academy/${track.slug}` : undefined },
                    { label: 'Network Basics' },
                ]}
                mb="24px"
            />

            <FollowGate>
            {stages.length > 0 && (
                <GlowCard glowOnHover={false} sx={{ mb: '24px' }}>
                    <Box sx={{ p: '24px', display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                        <ProgressRing value={progress} size={56} strokeWidth={4} />
                        <Box>
                            <Typography sx={{ color: colors.text.primary, fontFamily: '"Orbitron", sans-serif', fontWeight: 700 }}>
                                {solvedCount} / {stages.length} stages complete
                            </Typography>
                            <Typography sx={{ color: colors.text.secondary, fontSize: '0.9rem' }}>
                                Work through each stage in order. Every stage ends with a short quiz that unlocks the next one.
                            </Typography>
                        </Box>
                    </Box>
                </GlowCard>
            )}

            <Box sx={{ display: 'grid', gap: '14px' }}>
                {stages.map((stage, idx) => {
                    const content = getStageContent(category, stage.slug);
                    const isLockedForAnon = stage.locked && !token;
                    const isLockedForUser = stage.locked && !!token;
                    const clickable = !stage.locked;

                    const rowInner = (
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            p: '18px 20px',
                            opacity: stage.locked ? 0.6 : 1,
                        }}>
                            <Box sx={{
                                width: 40,
                                height: 40,
                                flexShrink: 0,
                                borderRadius: '50%',
                                display: 'grid',
                                placeItems: 'center',
                                fontFamily: '"Orbitron", sans-serif',
                                fontWeight: 800,
                                color: stage.solved ? colors.bg.primary : colors.text.primary,
                                background: stage.solved ? colors.gradient.primary : colors.bg.tertiary,
                                border: stage.solved ? 'none' : `1px solid ${colors.border.subtle}`,
                            }}>
                                {stage.solved ? <CheckCircleIcon /> : stage.locked ? <LockIcon fontSize="small" /> : idx + 1}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                                <Typography sx={{ color: colors.text.primary, fontWeight: 700 }}>
                                    {stage.title}
                                </Typography>
                                <Typography sx={{ color: colors.text.secondary, fontSize: '0.86rem' }}>
                                    {isLockedForAnon
                                        ? 'Sign up to unlock'
                                        : isLockedForUser
                                            ? 'Finish the previous stage to unlock'
                                            : content?.tagline || `${stage.points} pts`}
                                </Typography>
                            </Box>
                            {!stage.locked && !stage.solved && (
                                <PlayCircleOutlineIcon sx={{ color: colors.accent.cyan }} />
                            )}
                            <Typography sx={{ color: colors.text.muted, fontFamily: '"Share Tech Mono", monospace', fontSize: '0.8rem' }}>
                                {stage.points} pts
                            </Typography>
                        </Box>
                    );

                    return (
                        <GlowCard key={stage.id} glowOnHover={clickable}>
                            {clickable ? (
                                <Box component={RouterLink} to={`/academy/${category}/theory/${stage.slug}`} sx={{ textDecoration: 'none', display: 'block' }}>
                                    {rowInner}
                                </Box>
                            ) : isLockedForAnon ? (
                                <Box component={RouterLink} to="/register" sx={{ textDecoration: 'none', display: 'block' }}>
                                    {rowInner}
                                </Box>
                            ) : (
                                rowInner
                            )}
                        </GlowCard>
                    );
                })}
            </Box>
            </FollowGate>

            {loaded && !stages.length && (
                <GlowCard glowOnHover={false}>
                    <Box sx={{ p: '40px', textAlign: 'center' }}>
                        <Typography sx={{ color: colors.text.secondary }}>
                            No stages have been published for this track yet.
                        </Typography>
                    </Box>
                </GlowCard>
            )}

            {!loaded && (
                <Box sx={{ textAlign: 'center', marginTop: '40px' }}>
                    <CircularProgress size={60} />
                </Box>
            )}
        </PageContainer>
    );
};

export default TheoryOverview;
